import { vendorApi } from '@/lib/vendorApi';
import type { VendorAuthResponse, VendorUserDTO, VendorRole } from '@pms/shared';

export interface VendorHealth {
  windowHours: number;
  generatedAt: string;
  db: { ok: boolean };
  migrations: { latest: string | null };
  channelSync: {
    total: number;
    errors: number;
    errorRatePct: number;
    byStatus: Record<string, number>;
  };
  email: {
    total: number;
    sent: number;
    failed: number;
    failRatePct: number;
    byTemplate: Record<string, number>;
    byStatus: Record<string, number>;
  };
}

export interface VendorMetrics {
  windowHours: number;
  bucket: 'hour' | 'day';
  generatedAt: string;
  buckets: string[];
  series: {
    emailSent: number[];
    emailFailed: number[];
    syncSuccess: number[];
    syncFailed: number[];
  };
  emailByTemplate: { template: string; count: number; failed: number }[];
  syncByChannel: { channel: string; count: number; failed: number }[];
  topFailingHotels: { hotelId: string; hotel: string; failures: number }[];
}

export interface VendorEmailRow {
  id: string;
  time: string;
  templateType: string;
  status: string;
  recipient: string;
  subject: string | null;
  error: string | null;
  propertyId: string | null;
  bookingId: string | null;
}

export interface VendorEmailPage {
  total: number;
  rows: VendorEmailRow[];
}

export interface VendorActivityEvent {
  id: string;
  time: string;
  hotelId: string;
  hotel: string;
  unitId: string;
  channel: string;
  type: string;
  status: string;
  error: string | null;
}

export interface VendorActivityPage {
  total: number;
  rows: VendorActivityEvent[];
}

export interface VendorMetricsParams {
  windowHours?: number;
  bucket?: 'hour' | 'day';
}

export interface VendorEmailQuery {
  limit?: number;
  offset?: number;
  status?: string;
  templateType?: string;
  propertyId?: string;
  q?: string;
  fromDate?: string;
  toDate?: string;
}

export interface VendorActivityQuery {
  limit?: number;
  offset?: number;
  propertyId?: string;
  status?: string;
  channel?: string;
  type?: string;
  q?: string;
  fromDate?: string;
  toDate?: string;
}

export const vendorAuthApi = {
  login: async (email: string, password: string): Promise<VendorAuthResponse> =>
    (await vendorApi.post('/vendor/auth/login', { email, password })).data,
  logout: async (refreshToken: string): Promise<void> => {
    await vendorApi.post('/vendor/auth/logout', { refreshToken });
  },
  me: async (): Promise<{ vendorUserId: string; email: string; role: VendorRole }> =>
    (await vendorApi.get('/vendor/auth/me')).data,
  acceptInvite: async (
    token: string,
    password: string,
    fullName?: string,
  ): Promise<VendorAuthResponse> =>
    (await vendorApi.post('/vendor/auth/accept-invite', { token, password, fullName }))
      .data,
};

export const vendorObsApi = {
  health: async (): Promise<VendorHealth> =>
    (await vendorApi.get('/vendor/observability/health')).data,
  metrics: async (params: VendorMetricsParams = {}): Promise<VendorMetrics> =>
    (await vendorApi.get('/vendor/observability/metrics', { params })).data,
  emails: async (params: VendorEmailQuery = {}): Promise<VendorEmailPage> =>
    (await vendorApi.get('/vendor/observability/emails', { params })).data,
  activity: async (params: VendorActivityQuery = {}): Promise<VendorActivityPage> =>
    (await vendorApi.get('/vendor/observability/activity', { params })).data,
  hotelDiagnostics: async (id: string): Promise<Record<string, unknown>> =>
    (await vendorApi.get(`/vendor/observability/hotels/${id}/diagnostics`)).data,
};

export const vendorTeamApi = {
  list: async (): Promise<VendorUserDTO[]> =>
    (await vendorApi.get('/vendor/team')).data,
  invite: async (
    email: string,
    role: VendorRole,
  ): Promise<{ token: string; email: string; role: VendorRole }> =>
    (await vendorApi.post('/vendor/team/invite', { email, role })).data,
  setRole: async (id: string, role: VendorRole): Promise<VendorUserDTO> =>
    (await vendorApi.patch(`/vendor/team/${id}/role`, { role })).data,
  disable: async (id: string): Promise<VendorUserDTO> =>
    (await vendorApi.patch(`/vendor/team/${id}/disable`)).data,
  enable: async (id: string): Promise<VendorUserDTO> =>
    (await vendorApi.patch(`/vendor/team/${id}/enable`)).data,
};
